<template>
  <Card class="character">
    <div v-if="!route.meta.readOnly" class="inspirationIcon">
      <label>
        <input 
          type="checkbox"
          :checked="character.activeEffects.includes('inspiration')"
          @input="$emit('inspired', $event.target.checked)"
        />
        <span />
      </label>
    </div>
    <div v-else class="inspirationIcon" style="pointer-events: none">
      <label>
        <input 
          type="checkbox"
          :checked="character.activeEffects.includes('inspiration')"
        />
        <span />
      </label>
    </div>

    <div class="block">
      <h2 class="characterTitle title is-4">{{ character.name || 'Wanderer' }}</h2>
      <p class="classRaceSubtitle subtitle is-6">{{ [ race.name, className ].join(' ') }} ({{ difficulty.name }})</p>
    </div>
    <hr />

    <div class="block">
      <Stats :class="{ 'isReadOnly': route.meta.readOnly }" @health-clicked="openModal('modifyHealth')" />
      <hr />
    </div>

    <div class="block">
      <Abilities :class="{ 'isReadOnly': route.meta.readOnly }" @ability-clicked="openModal('abilityCheck', { savingThrow: $event })" />
      <hr />
    </div>

    <div class="block" v-if="character.features.length || race.features || race.featureChoices || characterClass.features || characterClass.featureChoices">
      <div class="card-header-title level is-mobile skillTitle">
        <div class="level-left">
          <div>
            Features
          </div>
        </div>
        <div v-if="!route.meta.readOnly" class="level-right">
          <div class="buttons are-small has-addons is-right">
            <button class="button" @click="scrollToSection('features')">Features</button>
            <button class="button" @click="scrollToSection('actions')">Actions</button>
          </div>
        </div>
      </div>
      <FeatureList />
      <hr />
    </div>

    <div v-if="characterClass.spellLevel || characterClass.spells?.length" class="block">
      <div class="card-header-title level is-mobile skillTitle">
        <div class="level-left">
          <div>
            Spells <small>(level {{ characterClass.spellLevel }})</small>
          </div>
        </div>
        <div v-if="!route.meta.readOnly" class="level level-right">
          <div class="buttons are-small has-addons is-right level-item">
            <button class="button" @click="scrollToSection('spells')">Spells</button>
            <button class="button" @click="openModal('modifySpellPoints')">Spend Points ({{ character.spellPoints }} of {{ characterClass.spellPoints }})</button>
          </div>
        </div>
        <div v-else class="level-right">
          {{ characterClass.spellPoints }} points
        </div>
      </div>
      <SpellCasting />
      <hr />
    </div>

    <div class="block">
      <div class="card-header-title level is-mobile skillTitle">
        <div class="level-left">
          <div>
            Skills
            <small v-if="skillToggleSelection === 'learned'">&nbsp;(hiding {{ Object.keys(skillList).length - character.skills.length }})</small>
            <small v-else-if="skillToggleSelection === 'none'">&nbsp;(hiding {{ Object.keys(skillList).length }})</small>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons are-small has-addons is-right">
            <button :class="{ 'button': true, 'is-primary': skillToggleSelection === 'all' }" @click="skillToggleSelection = 'all'">All</button>
            <button :class="{ 'button': true, 'is-primary': skillToggleSelection === 'learned' }" @click="skillToggleSelection = 'learned'">Learned</button>
            <!-- <button :class="{ 'button': true, 'is-primary': skillToggleSelection === 'none' }" @click="skillToggleSelection = 'none'">None</button> -->
          </div>
        </div>
      </div>
      <SkillList :filter="skillToggleSelection" @skill-clicked="openModal('abilityCheck', { skill: skillList[$event], key: $event })" />
      <hr />
    </div>

    <div class="block">
      <div class="card-header-title level is-mobile skillTitle">
        <div class="level-left">
          <div>
            Equipment
          </div>
        </div>
        <div v-if="!route.meta.readOnly" class="level-right">
          <div class="buttons are-small has-addons is-right">
            <button class="button" @click="scrollToSection('weapons')">Weapons</button>
            <button class="button" @click="scrollToSection('equipment')">Equipment</button>
          </div>
        </div>
      </div>
      <EquipmentList />
      <hr />
    </div>

    <div class="block">
      <div class="card-header-title level is-mobile skillTitle">
        <div class="level-left">
          <div>
            Notes
          </div>
        </div>
        <div v-if="!route.meta.readOnly" class="level-right">
          <div class="buttons are-small is-right">
            <button class="button" @click="openModal('editNotes')">Edit Notes</button>
          </div>
        </div>
      </div>
      <Notes />
      <hr />
    </div>

    <template v-slot:footer>
      <slot />
    </template>
  </Card>
</template>

<script>
import { defineComponent, provide, ref } from 'vue'
import { useRoute } from 'vue-router'

import useModal from '@/hooks/useModal'

import Abilities from '@/components/character/Abilities'
import SkillList from '@/components/character/SkillList'
import Stats from '@/components/character/Stats'
import FeatureList from '@/components/character/FeatureList'
import EquipmentList from '@/components/character/EquipmentList'
import SpellCasting from '@/components/character/SpellCasting'
import Notes from '@/components/character/Notes'
import Card from '@/components/Card'

import skillList from '@/data/skills'

export default defineComponent({
  props: {
    character: {
      type: Object,
      required: true
    },

    computed: {
      type: Object,
      required: true
    }
  },

  components: {
    Abilities,
    SkillList,
    Stats,
    SpellCasting,
    FeatureList,
    EquipmentList,
    Notes,
    Card
  },

  setup (props) {
    const route = useRoute()
    const { openModal } = useModal()

    provide('character', props.character)
    provide('computedCharacter', props.computed)

    const skillToggleSelection = ref('learned')

    const scrollToSection = (section) => {
      const el = document.querySelector(`#${section}Section`)
      if (!el) return

      const brect = el.getBoundingClientRect()

      window.scrollTo({
        top: brect.top,
        left: 0,
        behavior: 'smooth'
      })
    }

    const conditionallyOpenModal = (modal, options) => {
      if (route.meta.readOnly) return
      openModal(modal, options)
    }

    return {
      route,
      openModal: conditionallyOpenModal,
      scrollToSection,

      ...props.computed,
      skillToggleSelection,

      skillList
    }
  }
})
</script>

<style lang="sass" scoped>
.character
  .block
    &:last-of-type
      hr
        display: none

.characterTitle
  font-family: 'Playfair Display', serif

.classRaceSubtitle
  text-transform: capitalize

.skillTitle
  &:nth-last-child(2)
    margin-bottom: 0

.card-header-title
  margin-bottom: 1rem
  padding: 0

.link
  font-weight: normal

.button
  font-family: 'Roboto', sans-serif

.inspirationIcon
  position: absolute
  top: 1rem
  right: 1rem
  input
    position: absolute
    visibility: hidden
  input + span
    display: flex
    align-items: center
    justify-content: center
    width: 2.75rem
    height: 2.75rem
    cursor: pointer
    border-radius: 1000px
    font-size: 150%
    &::after
      content: '\e65f'
      color: #dbdbdb
      font-family: 'Material Icons'
      display: inline-flex
  input:checked + span
    background: var(--primary)
    &::after
      color: #fff

.isReadOnly
  pointer-events: none
</style>
